import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Painel do Operador (16/10/2026), no molde do Painel do Operador do BPO RH:
// tudo o que o operador tem no mês — entregas, tarefas, certificados, Monitor, fechamento do
// mês anterior e notas retroativas — das empresas ATIVAS em que ele é o responsável.
// Só leitura (api/fiscal/painel/); cada item leva ao módulo.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MAX_ITENS_GRUPO = 5;
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const dataBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const quando = (dias) => (dias === null || dias === undefined ? '' : dias < 0 ? `venceu há ${-dias} dia(s)` : dias === 0 ? 'vence hoje' : dias === 1 ? 'vence amanhã' : `em ${dias} dias`);

const Estado = ({ tipo, icone, children }) => <span className={`po-estado ${tipo}`}><i className={`fas ${icone}`} aria-hidden="true"></i>{children}</span>;
const Vazio = ({ texto }) => (<div className="po-vazio"><i className="fas fa-circle-check" aria-hidden="true"></i>{texto}</div>);

const Card = ({ id, icone, titulo, sub, count, countTipo, link, linkRotulo = 'Abrir módulo', children }) => {
  const badge = countTipo === 'bad' ? 'bg-danger' : countTipo === 'warn' ? 'bg-warning text-dark' : countTipo === 'ok' ? 'bg-success' : 'bg-light text-dark';
  return (
    <section id={id} className="po-card po-enter mb-3" aria-labelledby={`${id}-t`}>
      <header className="po-card-head">
        <h3 id={`${id}-t`}><i className={`fas ${icone}`} aria-hidden="true"></i>{titulo}</h3>
        {count !== undefined && <span className={`badge ${badge}`}>{count}</span>}
        {sub && <span className="po-sub">{sub}</span>}
        {link && <div className="ms-auto-wrap"><Link to={link} className="btn btn-sm btn-light"><i className="fas fa-arrow-up-right-from-square me-1" aria-hidden="true"></i>{linkRotulo}</Link></div>}
      </header>
      <div className="po-card-body">{children}</div>
    </section>
  );
};

const Grupo = ({ icone, rotulo, itens, link, cor }) => {
  const [aberto, setAberto] = useState(false);
  if (!itens.length) return null;
  const mostrar = aberto ? itens : itens.slice(0, MAX_ITENS_GRUPO);
  const resto = itens.length - mostrar.length;
  return (
    <div className="po-grupo">
      <div className="po-grupo-head">
        <i className={`fas ${icone} po-texto-${cor}`} aria-hidden="true"></i>{rotulo}
        <span className={`badge rounded-pill ${cor === 'bad' ? 'bg-danger' : 'bg-warning text-dark'}`}>{itens.length}</span>
        <Link to={link}>abrir <i className="fas fa-arrow-right ms-1" style={{ fontSize: '.55rem' }} aria-hidden="true"></i></Link>
      </div>
      {mostrar.map((it, i) => (
        <Link key={i} to={link} className="po-grupo-item" title={it.sub}><span className="text-truncate">{it.texto}</span><span className="po-sub">{it.sub}</span></Link>
      ))}
      {resto > 0 && <button type="button" className="po-link-mais" onClick={() => setAberto(true)}>… e mais {resto}</button>}
      {aberto && itens.length > MAX_ITENS_GRUPO && <button type="button" className="po-link-mais" onClick={() => setAberto(false)}>mostrar menos</button>}
    </div>
  );
};

const Tabela = ({ colunas, linhas, chave, vazio }) => {
  if (!linhas.length) return <Vazio texto={vazio} />;
  return (
    <table className="table po-table align-middle mb-0">
      <thead><tr>{colunas.map((c) => <th key={c.rotulo} style={c.estilo}>{c.rotulo}</th>)}</tr></thead>
      <tbody>{linhas.map((l) => <tr key={chave(l)}>{colunas.map((c) => <td key={c.rotulo} className={c.classe}>{c.render(l)}</td>)}</tr>)}</tbody>
    </table>
  );
};

const SITUACAO_ENTREGA = {
  vencida: ['bad', 'fa-circle-exclamation', 'Vencida'],
  hoje: ['hot', 'fa-fire', 'Vence hoje/amanhã'],
  em2dias: ['warn', 'fa-hourglass-half', 'Em 2 dias'],
  futura: ['neutro', 'fa-clock', 'No prazo'],
  justificada: ['warn', 'fa-comment-dots', 'Atrasada com obs.'],
  concluida: ['ok', 'fa-circle-check', 'Concluída'],
};
const SITUACAO_CERT = {
  vencido: ['bad', 'fa-circle-xmark', 'Vencido'],
  vence_30d: ['warn', 'fa-clock', 'Vence em até 30 dias'],
  arquivo_pendente: ['neutro', 'fa-upload', 'Arquivo pendente'],
  sem_validade: ['warn', 'fa-calendar-xmark', 'Sem validade'],
  sem_certificado: ['neutro', 'fa-minus', 'Sem certificado'],
};
const SITUACAO_MONITOR = {
  erro: ['bad', 'fa-triangle-exclamation', 'Erro no download'],
  desatualizada: ['warn', 'fa-clock-rotate-left', 'Desatualizada'],
  fora: ['neutro', 'fa-satellite-dish', 'Fora do Monitor'],
};
const SITUACAO_ACESSORIAS = {
  atrasada: ['bad', 'fa-circle-exclamation', 'Atrasada'],
  prazo_tecnico: ['warn', 'fa-hourglass-end', 'Prazo técnico'],
};

export default function FiscalPainel() {
  const agora = new Date();
  const [mes, setMes] = useState(agora.getMonth() + 1);
  const [ano, setAno] = useState(agora.getFullYear());
  const [dono, setDono] = useState('');
  const [busca, setBusca] = useState('');
  const [soEstourado, setSoEstourado] = useState(false);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/fiscal/painel/`, { params: { mes, ano, user_id: dono || undefined, _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar o painel'))
      .finally(() => setCarregando(false));
  }, [mes, ano, dono]);
  useEffect(() => { carregar(); }, [carregar]);

  // filtro por empresa (código, nome) e "só o que estourou", aplicado a todas as seções
  const f = useMemo(() => {
    const termo = semAcento(busca.trim());
    const bate = (x) => !termo || String(x.cod) === busca.trim() || semAcento(x.cliente).includes(termo);
    const d = dados || {};
    const sel = (lista, estourou) => (lista || []).filter((x) => bate(x) && (!soEstourado || estourou(x)));
    return {
      entregas: sel(d.entregas, (x) => ['vencida', 'hoje'].includes(x.situacao)),
      semEntrega: soEstourado ? [] : sel(d.sem_entrega, () => true),
      tarefas: sel(d.tarefas, (x) => x.situacao === 'vencida'),
      certificados: sel(d.certificados, (x) => x.situacao === 'vencido'),
      monitor: sel(d.monitor, (x) => x.situacao === 'erro'),
      fechamentos: soEstourado ? [] : sel(d.fechamentos, () => true),
      retroativas: soEstourado ? [] : sel(d.retroativas, () => true),
      acessorias: sel(d.acessorias, (x) => x.situacao === 'atrasada'),
    };
  }, [dados, busca, soEstourado]);

  const pendentesEntregas = f.entregas.filter((e) => !['concluida', 'justificada'].includes(e.situacao));
  const concluidas = (dados?.entregas || []).filter((e) => e.situacao === 'concluida').length;
  const totalEntregas = (dados?.entregas || []).length;
  const hoje = {
    vencidas: f.entregas.filter((e) => e.situacao === 'vencida'),
    hojeAmanha: f.entregas.filter((e) => e.situacao === 'hoje'),
    tarefas: f.tarefas.filter((t) => t.situacao === 'vencida'),
    certificados: f.certificados.filter((c) => c.situacao === 'vencido'),
    monitor: f.monitor.filter((m) => m.situacao === 'erro'),
    acessorias: f.acessorias.filter((a) => a.situacao === 'atrasada'),
  };
  const nCritico = Object.values(hoje).reduce((n, l) => n + l.length, 0);

  const kpis = [
    { id: 'pf-entregas', icone: 'fa-circle-exclamation', rotulo: 'Entregas vencidas', valor: hoje.vencidas.length, cor: 'c-bad' },
    { id: 'pf-entregas', icone: 'fa-fire', rotulo: 'Vencem hoje/amanhã', valor: hoje.hojeAmanha.length, cor: 'c-hot' },
    { id: 'pf-entregas', icone: 'fa-circle-check', rotulo: 'Entregas concluídas', valor: concluidas, sub: `de ${totalEntregas}`, cor: 'c-ok' },
    { id: 'pf-entregas', icone: 'fa-calendar-plus', rotulo: 'Mês não gerado', valor: f.semEntrega.length, sub: 'empresa(s) sem entrega', cor: 'c-warn' },
    { id: 'pf-acessorias', icone: 'fa-flag', rotulo: 'Acessórias em atraso', valor: f.acessorias.length, sub: `${hoje.acessorias.length} atrasada(s)`, cor: 'c-bad' },
    { id: 'pf-tarefas', icone: 'fa-list-check', rotulo: 'Tarefas abertas', valor: f.tarefas.length, sub: `${hoje.tarefas.length} vencida(s)`, cor: 'c-navy' },
    { id: 'pf-certificados', icone: 'fa-certificate', rotulo: 'Certificados', valor: f.certificados.length, sub: `${hoje.certificados.length} vencido(s)`, cor: 'c-warn' },
    { id: 'pf-monitor', icone: 'fa-satellite-dish', rotulo: 'Monitor', valor: f.monitor.length, sub: `${hoje.monitor.length} com erro`, cor: 'c-bad' },
    { id: 'pf-fechamentos', icone: 'fa-file-excel', rotulo: 'Fechamentos pendentes', valor: f.fechamentos.length, sub: dados ? `de ${dados.competencia_fechamento}` : '', cor: 'c-navy' },
  ];
  const irPara = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const anos = [ano - 1, ano, ano + 1];

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-gauge-high header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Painel do Operador — Fiscal</h2>
          </div>
          <div className="gr-toolbar">
            {dados?.pode_escolher && (
              <select className="po-select" value={dono} onChange={(e) => setDono(e.target.value)} aria-label="De quem é este painel" title="De quem é este painel">
                <option value="">Minhas empresas</option>
                <option value="todos">Todos os operadores</option>
                {(dados.operadores || []).map((o) => <option key={o.user_id} value={o.user_id}>{o.nome} ({o.empresas})</option>)}
              </select>
            )}
            <select className="po-select" value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mês">
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select className="po-select" value={ano} onChange={(e) => setAno(Number(e.target.value))} aria-label="Ano">
              {anos.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
          </div>
        </div>

        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}

        {!dados ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-2x mb-3" style={{ color: '#00b5cc' }}></i><div>Montando o seu painel...</div></div>
        ) : (
          <>
            <div className="alert alert-light border small py-2">
              <i className="fas fa-circle-info me-2 text-primary"></i>
              <b>{dados.dono.nome}</b> · {dados.dono.empresas} empresa(s) ativa(s) sob responsabilidade · competência <b>{MESES[mes - 1]}/{ano}</b>.
              {dados.dono.eu && !dados.dono.empresas && <> Você não é o responsável por nenhuma empresa na Base Fiscal: peça para ajustar o responsável no lápis da empresa.</>}
            </div>

            <div className="fz-filtros">
              <input className="po-busca" placeholder="Filtrar por código ou nome da empresa" value={busca} onChange={(e) => setBusca(e.target.value)} />
              <label className="po-toggle"><input type="checkbox" checked={soEstourado} onChange={(e) => setSoEstourado(e.target.checked)} />só o que estourou</label>
            </div>

            <div className="po-kpis">
              {kpis.map((k) => (
                <button key={k.rotulo} type="button" className={`po-kpi ${k.cor} ${k.valor ? '' : 'po-zero'}`} onClick={() => irPara(k.id)}>
                  <div className="po-kpi-rotulo"><i className={`fas ${k.icone}`} aria-hidden="true"></i>{k.rotulo}</div>
                  <div className="po-kpi-valor">{k.valor}</div>
                  {k.sub && <div className="po-kpi-sub">{k.sub}</div>}
                </button>
              ))}
            </div>

            <section className="po-card po-enter mb-3" aria-labelledby="pf-hoje-t">
              <header className={`po-card-head ${nCritico ? 'critico' : ''}`}>
                <h3 id="pf-hoje-t"><i className={`fas ${nCritico ? 'fa-bell' : 'fa-mug-hot'}`} aria-hidden="true"></i>{nCritico ? 'Precisa de você hoje' : 'Nada estourado hoje'}</h3>
                {nCritico > 0 && <span className="badge bg-light text-dark">{nCritico}</span>}
              </header>
              <div className="po-card-body pad">
                {nCritico === 0 ? <Vazio texto="Nenhuma entrega vencida, nada vencendo hoje ou amanhã, sem certificado vencido nem download com erro." /> : (
                  <div className="row g-3">
                    <div className="col-lg-4"><Grupo icone="fa-circle-exclamation" cor="bad" rotulo="Entregas vencidas" link="/Fiscal/Entregas"
                      itens={hoje.vencidas.map((e) => ({ texto: `${e.cod} · ${e.cliente}`, sub: quando(e.dias) }))} /></div>
                    <div className="col-lg-4"><Grupo icone="fa-fire" cor="warn" rotulo="Vencem hoje ou amanhã" link="/Fiscal/Entregas"
                      itens={hoje.hojeAmanha.map((e) => ({ texto: `${e.cod} · ${e.cliente}`, sub: quando(e.dias) }))} /></div>
                    <div className="col-lg-4"><Grupo icone="fa-flag" cor="bad" rotulo="Acessórias em atraso" link="/Fiscal/TarefasAcessorias"
                      itens={hoje.acessorias.map((a) => ({ texto: `${a.cod} · ${a.cliente}`, sub: `${a.atividade} · ${quando(a.dias)}` }))} /></div>
                    <div className="col-lg-4"><Grupo icone="fa-list-check" cor="bad" rotulo="Tarefas vencidas" link="/Fiscal/Tarefas"
                      itens={hoje.tarefas.map((t) => ({ texto: t.assunto, sub: quando(t.dias) }))} /></div>
                    <div className="col-lg-4"><Grupo icone="fa-certificate" cor="bad" rotulo="Certificados vencidos" link="/Fiscal/Certificados"
                      itens={hoje.certificados.map((c) => ({ texto: `${c.cod} · ${c.cliente}`, sub: c.validade }))} /></div>
                    <div className="col-lg-4"><Grupo icone="fa-satellite-dish" cor="bad" rotulo="Download com erro" link="/Fiscal/Monitor"
                      itens={hoje.monitor.map((m) => ({ texto: `${m.cod} · ${m.cliente}`, sub: m.detalhe }))} /></div>
                  </div>
                )}
              </div>
            </section>

            <Card id="pf-entregas" icone="fa-calendar-check" titulo="Entregas do mês" count={pendentesEntregas.length}
              countTipo={hoje.vencidas.length ? 'bad' : pendentesEntregas.length ? 'warn' : 'ok'} sub={`${concluidas} de ${totalEntregas} concluída(s)`} link="/Fiscal/Entregas">
              {f.semEntrega.length > 0 && (
                <div className="alert alert-warning small m-3 mb-0">
                  <i className="fas fa-calendar-plus me-2"></i><b>{f.semEntrega.length}</b> empresa(s) ainda sem entrega em {MESES[mes - 1]}/{ano}
                  ({f.semEntrega.slice(0, 4).map((e) => e.cliente).join(', ')}{f.semEntrega.length > 4 ? '…' : ''}). Use <Link to="/Fiscal/Entregas">"Gerar mês"</Link>.
                </div>
              )}
              <Tabela vazio="Nenhuma entrega pendente nesta competência." chave={(e) => e.id} linhas={pendentesEntregas}
                colunas={[
                  { rotulo: 'Prazo', render: (e) => <span className="fz-num">{dataBr(e.data_prevista)}</span>, estilo: { width: 110 } },
                  { rotulo: 'Empresa', render: (e) => <><div className="fw-semibold">{e.cliente}</div><div className="fz-sub">{e.cod}</div></> },
                  ...(dados.dono.user_id ? [] : [{ rotulo: 'Responsável', render: (e) => e.responsavel || '—', classe: 'small' }]),
                  { rotulo: 'Situação', render: (e) => { const [t, i, txt] = SITUACAO_ENTREGA[e.situacao]; return <Estado tipo={t} icone={i}>{txt}</Estado>; } },
                  { rotulo: 'Quando', render: (e) => quando(e.dias), classe: 'small' },
                ]} />
            </Card>

            <Card id="pf-acessorias" icone="fa-flag" titulo="Acessórias em atraso" count={f.acessorias.length}
              countTipo={hoje.acessorias.length ? 'bad' : f.acessorias.length ? 'warn' : 'ok'}
              sub={dados.acessorias_sincronizado_em ? `obrigações controladas vencidas e sem entrega · espelho sincronizado em ${dados.acessorias_sincronizado_em}` : 'obrigações controladas vencidas e sem entrega'}
              link="/Fiscal/TarefasAcessorias">
              <Tabela vazio="Nenhuma obrigação do Acessórias em atraso nas suas empresas." chave={(a) => a.id} linhas={f.acessorias}
                colunas={[
                  { rotulo: 'Prazo', render: (a) => <span className="fz-num">{dataBr(a.prazo)}</span>, estilo: { width: 110 } },
                  { rotulo: 'Empresa', render: (a) => <><div className="fw-semibold">{a.cliente}</div><div className="fz-sub">{a.cod}</div></> },
                  { rotulo: 'Atividade', render: (a) => <><div className="fw-semibold">{a.atividade}</div><div className="fz-sub">{a.competencia ? `competência ${dataBr(a.competencia)}` : ''}</div></> },
                  { rotulo: 'Situação', render: (a) => { const [t, i, txt] = SITUACAO_ACESSORIAS[a.situacao]; return <>{<Estado tipo={t} icone={i}>{txt}</Estado>}{a.multa && <Estado tipo="bad" icone="fa-gavel">multa</Estado>}</>; } },
                  { rotulo: 'Responsável', render: (a) => a.responsavel || '—', classe: 'small' },
                  { rotulo: 'Quando', render: (a) => quando(a.dias), classe: 'small' },
                ]} />
            </Card>

            <Card id="pf-tarefas" icone="fa-list-check" titulo="Tarefas Extras" count={f.tarefas.length}
              countTipo={hoje.tarefas.length ? 'bad' : f.tarefas.length ? 'warn' : 'ok'} link="/Fiscal/Tarefas">
              <Tabela vazio="Nenhuma tarefa aberta." chave={(t) => t.id} linhas={f.tarefas}
                colunas={[
                  { rotulo: 'Prazo', render: (t) => <span className={`fz-num ${t.situacao === 'vencida' ? 'text-danger fw-semibold' : t.situacao === 'proxima' ? 'fz-prazo-proximo' : ''}`}>{dataBr(t.prazo)}</span>, estilo: { width: 110 } },
                  { rotulo: 'Assunto', render: (t) => <><div className="fw-semibold">{t.assunto}</div><div className="fz-sub">{t.cod ? `${t.cod} · ${t.cliente}` : 'sem empresa'}</div></> },
                  ...(dados.dono.user_id ? [] : [{ rotulo: 'Responsável', render: (t) => t.responsavel || '—', classe: 'small' }]),
                  { rotulo: 'Status', render: (t) => t.status, classe: 'small' },
                  { rotulo: 'Quando', render: (t) => quando(t.dias), classe: 'small' },
                ]} />
            </Card>

            <Card id="pf-certificados" icone="fa-certificate" titulo="Certificados A1" count={f.certificados.length}
              countTipo={hoje.certificados.length ? 'bad' : f.certificados.length ? 'warn' : 'ok'} sub="vencidos, vencendo ou sem arquivo" link="/Fiscal/Certificados">
              <Tabela vazio="Todos os certificados das suas empresas estão em dia." chave={(c) => c.cod} linhas={f.certificados}
                colunas={[
                  { rotulo: 'Empresa', render: (c) => <><div className="fw-semibold">{c.cliente}</div><div className="fz-sub">{c.cod}</div></> },
                  { rotulo: 'Situação', render: (c) => { const [t, i, txt] = SITUACAO_CERT[c.situacao] || SITUACAO_CERT.sem_validade; return <Estado tipo={t} icone={i}>{txt}</Estado>; } },
                  { rotulo: 'Validade', render: (c) => <span className="fz-num">{c.validade || '—'}</span>, classe: 'small' },
                ]} />
            </Card>

            <Card id="pf-monitor" icone="fa-satellite-dish" titulo="Monitor de NFS-e" count={f.monitor.length}
              countTipo={hoje.monitor.length ? 'bad' : f.monitor.length ? 'warn' : 'ok'} sub="erro, sem download há mais de 7 dias ou fora do Monitor" link="/Fiscal/Monitor">
              <Tabela vazio="Downloads em dia em todas as suas empresas com certificado." chave={(m) => m.cod} linhas={f.monitor}
                colunas={[
                  { rotulo: 'Empresa', render: (m) => <><div className="fw-semibold">{m.cliente}</div><div className="fz-sub">{m.cod}{m.ultimo_nsu != null ? ` · NSU ${m.ultimo_nsu}` : ''}</div></> },
                  { rotulo: 'Situação', render: (m) => { const [t, i, txt] = SITUACAO_MONITOR[m.situacao]; return <Estado tipo={t} icone={i}>{txt}</Estado>; } },
                  { rotulo: 'Detalhe', render: (m) => m.detalhe, classe: 'small' },
                ]} />
            </Card>

            <Card id="pf-fechamentos" icone="fa-file-excel" titulo={`Fechamento de ${dados.competencia_fechamento}`} count={f.fechamentos.length}
              countTipo={f.fechamentos.length ? 'warn' : 'ok'} sub="empresas sem fechamento concluído do mês anterior" link="/Fiscal/Fechamento">
              <Tabela vazio="Todas as suas empresas já têm o fechamento do mês anterior." chave={(x) => x.cod} linhas={f.fechamentos}
                colunas={[
                  { rotulo: 'Empresa', render: (x) => <><div className="fw-semibold">{x.cliente}</div><div className="fz-sub">{x.cod}</div></> },
                  { rotulo: 'Situação', render: (x) => <Estado tipo={x.status === 'Erro' ? 'bad' : 'neutro'} icone={x.status === 'Erro' ? 'fa-triangle-exclamation' : 'fa-minus'}>{x.status}</Estado> },
                ]} />
            </Card>

            <Card id="pf-retroativas" icone="fa-clock-rotate-left" titulo="Notas retroativas" count={f.retroativas.length}
              countTipo={f.retroativas.length ? 'warn' : 'ok'} sub="detectadas nos últimos 30 dias" link="/Fiscal/Monitor" linkRotulo="Abrir Monitor">
              <Tabela vazio="Nenhuma nota retroativa nos últimos 30 dias." chave={(r) => r.id} linhas={f.retroativas}
                colunas={[
                  { rotulo: 'Empresa', render: (r) => <><div className="fw-semibold">{r.cliente}</div><div className="fz-sub">{r.cod}</div></> },
                  { rotulo: 'Nota', render: (r) => <><span className="fz-mono">{r.numero || '—'}</span> <Estado tipo={r.emitida ? 'warn' : 'neutro'} icone="fa-file">{r.emitida ? 'emitida' : 'recebida'}</Estado></> },
                  { rotulo: 'Emissão', render: (r) => r.data_emissao, classe: 'small' },
                  { rotulo: 'Competência', render: (r) => r.competencia, classe: 'small' },
                  { rotulo: 'Detectada', render: (r) => r.detectado_em, classe: 'small' },
                ]} />
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
